import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useContest } from '../../contexts/ContestContext';
import { useThemeClassName, useTheme } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import StyledButton from '../buttons/StyledButton';

import styles from '../../../styles/partials/sport-participations-stats.module.scss';

const propTypes = {
    stats: AppPropTypes.stats,
    points: PropTypes.number,
    itemClassName: PropTypes.string,

    titleClassName: PropTypes.string,
    className: PropTypes.string,
};

const defaultProps = {
    stats: null,
    points: null,
    itemClassName: null,
    titleClassName: null,
    className: null,
};

function SportParticipationsStats({ stats, points, itemClassName, titleClassName, className }) {
    const route = useUrlGenerator();

    const { name: theme = null, isVariant } = useTheme();
    const themeClassName = useThemeClassName(styles);

    const {
        access: { profile = null },
        outcome: { hasLeaderboard = false },
    } = useContest();
    const { name = null, firstName = null, lastName = null } = useUser();
    const finalName =
        firstName !== null
            ? `${firstName}${lastName !== null && lastName.length > 0 ? ` ${lastName[0]}.` : ''}`
            : name;

    const { validAnswers, prizes, participations, leaderboardPosition = null } = stats || {};

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            <div className={styles.card}>
                <div className={styles.inner}>
                    <h2
                        className={classNames([
                            styles.title,
                            {
                                [titleClassName]: titleClassName !== null,
                            },
                        ])}
                    >
                        <FormattedMessage
                            defaultMessage="My stats"
                            description="Account section title"
                        />
                    </h2>
                    <ul className={styles.list}>
                        {profile !== null ? (
                            <li
                                className={classNames([
                                    styles.item,
                                    {
                                        [itemClassName]: itemClassName,
                                    },
                                ])}
                            >
                                <Button
                                    href={`${route('profile')}?next=${route('account')}`}
                                    className={styles.itemButton}
                                >
                                    <span className={styles.name}>
                                        <FormattedMessage
                                            defaultMessage="Name"
                                            description="Row label"
                                        />
                                    </span>
                                    <span className={styles.value}>{finalName}</span>
                                    <span className={styles.icon}>
                                        <FontAwesomeIcon icon={faPencil} />
                                    </span>
                                </Button>
                            </li>
                        ) : null}

                        <li
                            className={classNames([
                                styles.item,
                                {
                                    [itemClassName]: itemClassName,
                                },
                            ])}
                        >
                            <span className={styles.name}>
                                <FormattedMessage
                                    defaultMessage="Games played"
                                    description="Nhl participations stats name"
                                />
                            </span>
                            <span className={styles.value}>{participations || 0}</span>
                        </li>
                        <li
                            className={classNames([
                                styles.item,
                                {
                                    [itemClassName]: itemClassName,
                                },
                            ])}
                        >
                            <span className={styles.name}>
                                {isVariant('mvp') || theme === 'nfl' ? (
                                    <FormattedMessage
                                        defaultMessage="Right picks"
                                        description="Nhl participations stats name"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Right calls"
                                        description="Nhl participations stats name"
                                    />
                                )}
                            </span>
                            <span className={styles.value}>{validAnswers || 0}</span>
                        </li>
                        <li
                            className={classNames([
                                styles.item,
                                {
                                    [itemClassName]: itemClassName,
                                },
                            ])}
                        >
                            <span className={styles.name}>
                                <FormattedMessage
                                    defaultMessage="Points earned"
                                    description="Nhl participations stats name"
                                />
                            </span>
                            <span className={styles.value}>{points || 0}</span>
                        </li>
                        <li
                            className={classNames([
                                styles.item,
                                {
                                    [itemClassName]: itemClassName,
                                },
                            ])}
                        >
                            <span className={styles.name}>
                                <FormattedMessage
                                    defaultMessage="Prizes won"
                                    description="Nhl participations stats name"
                                />
                            </span>
                            <span className={styles.value}>{prizes || 0}</span>
                        </li>
                        {hasLeaderboard && leaderboardPosition !== null ? (
                            <li
                                className={classNames([
                                    styles.item,
                                    {
                                        [itemClassName]: itemClassName,
                                    },
                                ])}
                            >
                                <span className={styles.name}>
                                    <FormattedMessage
                                        defaultMessage="Your global rank"
                                        description="Leaderboard label"
                                    />
                                </span>
                                <span className={styles.value}>{leaderboardPosition || 0}</span>
                            </li>
                        ) : null}
                    </ul>
                </div>
                <div className={styles.bottom}>
                    {hasLeaderboard ? (
                        <StyledButton
                            href={route('leaderboard')}
                            colored
                            className={styles.leaderboardButton}
                        >
                            <FormattedMessage
                                defaultMessage="Consult the leaderboard"
                                description="Button label"
                            />
                        </StyledButton>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

SportParticipationsStats.defaultProps = defaultProps;
SportParticipationsStats.propTypes = propTypes;

export default SportParticipationsStats;
